import type { ReactElement } from "react";
import { renderServiceChips, servicesForMovie } from "../lib/movieDisplay.js";
import { sanitizePosterUrl } from "../lib/utils.js";
import { listKey } from "../firebase.js";
import { STATUS_ORDER, STATUS_LABELS } from "../store/useAppStore.js";
import type { StatusKey, WatchlistItem } from "../types/index.js";

export interface TitleCardProps {
  movie: WatchlistItem;
  /** When true, show who added the title (shared lists). */
  showAddedBy?: boolean;
  /** Current viewer — used for “Added by you” when `addedByUid` matches. */
  viewerUid?: string | null;
  /** Display name or email local part — legacy shared items when you are the list owner. */
  viewerDisplayName?: string | null;
  /** Firebase Auth profile image for the viewer (shared “added by you”). */
  viewerPhotoUrl?: string | null;
  /** `sharedLists/{id}.ownerId` — legacy rows without `addedBy*` infer owner vs member. */
  sharedListOwnerId?: string | null;
  /** `users/{sharedListOwnerId}.photoURL` for “added by list owner” when you are not the owner. */
  sharedListOwnerPhotoUrl?: string | null;
  userCountryCode: string;
  statusOpenKey: string | null;
  onSetStatusOpenKey: (key: string | null) => void;
  onStatusChange: (movie: WatchlistItem, status: StatusKey) => void;
  onOpenModal: (movie: WatchlistItem) => void;
  onRequestRemove: (movie: WatchlistItem) => void;
  /** Whether this title is currently favorited by the viewer. */
  isFavorite?: boolean;
  /** Called when the heart button is clicked. Undefined = heart button not shown. */
  onToggleFavorite?: () => void;
}

export function TitleCard({
  movie: m,
  showAddedBy = false,
  viewerUid = null,
  viewerDisplayName = null,
  viewerPhotoUrl = null,
  sharedListOwnerId = null,
  sharedListOwnerPhotoUrl = null,
  userCountryCode,
  statusOpenKey,
  onSetStatusOpenKey,
  onStatusChange,
  onOpenModal,
  onRequestRemove,
  isFavorite = false,
  onToggleFavorite,
}: TitleCardProps) {
  const key = listKey(m);
  const menuOpen = statusOpenKey === key;
  const isPending = m.tmdbPending === true || /^tt\d{7,}$/.test(m.title);
  const thumbSrc = sanitizePosterUrl(m.thumb);
  const thumbHTML = isPending ? (
    <div className="thumb-placeholder">
      <svg viewBox="0 0 24 24" width="40" height="40" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
        <rect x="2" y="4" width="20" height="16" rx="2" />
        <rect x="2" y="4" width="4" height="3" />
        <rect x="2" y="10.5" width="4" height="3" />
        <rect x="18" y="4" width="4" height="3" />
        <rect x="18" y="10.5" width="4" height="3" />
        <line x1="6" y1="4" x2="6" y2="20" />
        <line x1="18" y1="4" x2="18" y2="20" />
        <path d="M10 9l4 3-4 3V9z" fill="currentColor" stroke="none" />
      </svg>
    </div>
  ) : thumbSrc ? (
    <>
      <img
        src={thumbSrc}
        alt={`${m.title} trailer thumbnail`}
        loading="lazy"
        onError={(e) => {
          e.currentTarget.style.display = "none";
          const ph = e.currentTarget.nextElementSibling;
          if (ph instanceof HTMLElement) ph.style.display = "flex";
        }}
      />
      <div className="thumb-placeholder" style={{ display: "none" }}>
        {m.title}
      </div>
    </>
  ) : (
    <div className="thumb-placeholder">{m.title}</div>
  );

  const yearStr = m.year ? m.year : "—";
  const badgeClass = m.type === "show" ? "badge-show" : "badge-movie";
  const badgeLabel = isPending ? "?" : m.type === "show" ? "TV" : "Film";
  const langRaw = m.originalLanguage?.trim();
  const langCode =
    langRaw && langRaw.toLowerCase() !== "en" ? langRaw.slice(0, 2).toUpperCase() : null;
  const serviceChips = renderServiceChips(servicesForMovie(m, userCountryCode), { limit: 3 });
  const serviceRow = serviceChips ? (
    <div className="service-row" dangerouslySetInnerHTML={{ __html: serviceChips }} />
  ) : null;

  function sharedListAddedByLine(): string {
    const name = m.addedByDisplayName?.trim();
    if (name) return `Added by ${name}`;
    if (m.addedByUid) {
      if (viewerUid && m.addedByUid === viewerUid) return "Added by you";
      return "Added by another member";
    }
    // Legacy rows (no per-item attribution): infer from list owner.
    if (sharedListOwnerId && viewerUid && sharedListOwnerId === viewerUid) {
      const who = viewerDisplayName?.trim() || "you";
      return `Added by ${who}`;
    }
    if (sharedListOwnerId && viewerUid && sharedListOwnerId !== viewerUid) {
      return "Added by list owner";
    }
    return "Added by unknown";
  }

  const addedByAriaLabel = sharedListAddedByLine();

  function addedByAvatar(): { src: string; initial: string } | null {
    if (!showAddedBy) return null;
    if (m.addedByUid && viewerUid && m.addedByUid === viewerUid) {
      // Prefer Auth photo; fall back to merged `users/{uid}` (e.g. Auth missing photoURL in session).
      const src = sanitizePosterUrl(viewerPhotoUrl ?? m.addedByPhotoUrl ?? "");
      const label = viewerDisplayName?.trim() || m.addedByDisplayName?.trim() || "";
      return { src, initial: (label || "?").charAt(0).toUpperCase() };
    }
    if (m.addedByUid) {
      const src = sanitizePosterUrl(m.addedByPhotoUrl ?? "");
      return { src, initial: (m.addedByDisplayName || "?").charAt(0).toUpperCase() };
    }
    if (sharedListOwnerId && viewerUid && sharedListOwnerId === viewerUid) {
      const src = sanitizePosterUrl(viewerPhotoUrl ?? "");
      return { src, initial: (viewerDisplayName || "?").charAt(0).toUpperCase() };
    }
    if (sharedListOwnerId && viewerUid && sharedListOwnerId !== viewerUid) {
      const src = sanitizePosterUrl(sharedListOwnerPhotoUrl ?? "");
      return { src, initial: "O" };
    }
    return { src: "", initial: "?" };
  }

  const avatarBadge = showAddedBy ? addedByAvatar() : null;

  const s = m.status || "to-watch";
  const displayStatus = s;
  const statusTabKey = s === "watched" ? "watched" : "to-watch";
  const statusIcons: Record<string, ReactElement> = {
    "to-watch": (
      <>
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" fill="none" />
        <circle cx="12" cy="12" r="3" fill="none" />
      </>
    ),
    watched: <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />,
  };
  const iconKey: keyof typeof statusIcons = displayStatus === "watched" ? "watched" : "to-watch";

  const useFill = displayStatus === "watched";

  return (
    <div
      className="card"
      data-watchlist-movie-key={key}
      role="button"
      tabIndex={0}
      aria-label={`Play trailer for ${m.title}`}
      onClick={(e) => {
        const t = e.target;
        if (
          t instanceof Element &&
          (t.closest(".status-badge-wrap") ||
            t.closest(".card-delete-btn") ||
            t.closest(".btn-favorite"))
        )
          return;
        onOpenModal(m);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") onOpenModal(m);
      }}
    >
      <div className="thumb-wrap">
        <button
          type="button"
          className="card-delete-btn"
          aria-label="Remove from list"
          title="Remove"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            onRequestRemove(m);
          }}
        >
          &#215;
        </button>
        <div className="status-badge-wrap">
          <button
            type="button"
            className={`status-badge status-${displayStatus}`}
            aria-label="Change status"
            title="Change status"
            data-status={displayStatus}
            aria-haspopup="true"
            aria-expanded={menuOpen}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onSetStatusOpenKey(menuOpen ? null : key);
            }}
          >
            <svg
              viewBox="0 0 24 24"
              fill={useFill ? "currentColor" : "none"}
              stroke="currentColor"
              strokeWidth="2"
            >
              {statusIcons[iconKey]}
            </svg>
          </button>
          <div
            className={`status-dropdown${menuOpen ? " open" : ""}`}
            role="menu"
            aria-label="Move to"
          >
            {STATUS_ORDER.map((st) => (
              <button
                key={st}
                type="button"
                className={`status-dropdown-item${st === statusTabKey ? " active" : ""}`}
                role="menuitem"
                data-status={st}
                onClick={(e) => {
                  e.stopPropagation();
                  const raw = m.status || "to-watch";
                  const current: StatusKey = raw === "watched" ? "watched" : "to-watch";
                  if (st !== current) onStatusChange(m, st);
                  onSetStatusOpenKey(null);
                }}
              >
                {STATUS_LABELS[st]}
              </button>
            ))}
          </div>
        </div>
        {thumbHTML}
        {onToggleFavorite && m.status === "watched" ? (
          <button
            type="button"
            className={`btn-favorite${isFavorite ? " btn-favorite--active" : ""}`}
            aria-label={isFavorite ? "Remove from favorites" : "Add to favorites"}
            title={isFavorite ? "Remove from favorites" : "Add to favorites"}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onToggleFavorite();
            }}
          >
            {isFavorite ? "♥" : "♡"}
          </button>
        ) : null}
        <div className="thumb-overlay" />
        <div className="play-btn">
          <svg viewBox="0 0 24 24">
            <path d="M8 5v14l11-7z" />
          </svg>
        </div>
      </div>
      <div className="card-info">
        <div className="card-title" dir={/[\u0590-\u05FF]/.test(m.title) ? "rtl" : undefined}>
          {isPending && m.imdbId ? (
            <a
              href={`https://www.imdb.com/title/${m.imdbId}/`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              style={{ color: "inherit", textDecoration: "underline", textDecorationStyle: "dotted" }}
            >
              {m.title}
            </a>
          ) : m.title}
        </div>
        <div className="card-meta">
          <span className="card-meta-main">
            <span className={`badge ${badgeClass}`}>{badgeLabel}</span>
            {isPending ? <span className="badge" style={{ background: "var(--color-text-muted)", opacity: 0.7 }}>Pending</span> : null}
            {!isPending && langCode ? <span className="badge badge-lang">{langCode}</span> : null}
            {isPending ? null : <>{yearStr} &nbsp;·&nbsp; {m.genre}</>}
          </span>
          {avatarBadge ? (
            <div
              className="card-added-by-avatar"
              role="img"
              aria-label={addedByAriaLabel}
              title={addedByAriaLabel}
            >
              {avatarBadge.src ? (
                <img
                  src={avatarBadge.src}
                  alt=""
                  className="card-added-by-avatar-img"
                  loading="lazy"
                  referrerPolicy="no-referrer"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
              ) : null}
              <span className="card-added-by-avatar-initial">{avatarBadge.initial}</span>
            </div>
          ) : null}
        </div>
        {serviceRow}
      </div>
    </div>
  );
}
